import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import get from 'lodash/get';
import MySubscription from '../my-subscription';
import { EditIcon, Arrow, User, NewUserIcon, PhoneIcon, MailIcon, GenderIcon, Calender, EmptyCalenderIcon, LocationPinIcon, CityIcon, FillDownArrow, SpinnerIcon, TimerIcon, TickIcon, Close } from './icons';
import './profile-page.m.css';
import BirthDatePicker from './date-picker';
import { getEpaperEditions, getEpaperPreferences, updateEpaperPreferences } from "../../api";
import { putUserDetails, getUserDetails, changeEmailOtp, changePhoneOtp, verifySsoCode, clearAuthCookies, getAccessToken, performClientLogout } from "../../helper/utils";
import { setUserDetails, setIsLogin } from "../../store/actions";
import { Snackbar } from "../../common/snackbar";
import { addLogoutToDataLayer } from "../../helper/datalayer-utils";

const ProfileField = ({ label, value, type, prefixBlock, requiresOtp, icon, error, errorMessage, otpError, otpErrorMessage, isUpdating, onValidate, onSave, onSendOtp, onVerifyOtp, isRestricted, restrictedMessage }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState('');
  const [timer, setTimer] = useState(30);
  const inputRef = useRef(null);

  useEffect(() => {
    let interval;
    if (showOtp && timer > 0) {
      interval = setInterval(() => setTimer(t => t - 1), 1000);
    } else if (timer === 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [showOtp, timer]);

  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
    }
  }, [value, isEditing]);

  const handleEditClick = (e) => {
    e.preventDefault();
    if (isRestricted) {
      window.dispatchEvent(new CustomEvent("show-snackbar", { detail: { type: "failure", message: restrictedMessage } }));
      return;
    }
    setIsEditing(true);
    setEditValue(value);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setShowOtp(false);
    setEditValue(value);
    if (onValidate) onValidate(value, true); // true to reset error
  };

  const handleSaveClick = async (e) => {
    if (e) e.preventDefault();
    const trimmedEditValue = typeof editValue === 'string' ? editValue.trim() : editValue;
    const trimmedValue = typeof value === 'string' ? value.trim() : value;

    if (onValidate) {
      const isValid = onValidate(trimmedEditValue);
      if (!isValid) return;
    }

    if (requiresOtp && trimmedEditValue !== trimmedValue) {
      if (onSendOtp) {
        try {
          await onSendOtp(trimmedEditValue);
          setShowOtp(true);
          setTimer(30);
          setOtp('');
        } catch (e) {}
      }
    } else if (trimmedEditValue !== trimmedValue) {
      try {
        await onSave(trimmedEditValue);
        setIsEditing(false);
      } catch(e) {}
    } else {
      setIsEditing(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();
    const trimmedEditValue = typeof editValue === 'string' ? editValue.trim() : editValue;
    if (onVerifyOtp) {
      try {
        await onVerifyOtp(trimmedEditValue, otp);
        setIsEditing(false);
        setShowOtp(false);
      } catch (e) {}
    }
  };

  return (
    <div styleName="info-container" style={{ position: 'relative', ...(isEditing ? { borderBottom: 'none', paddingBottom: '15px' } : {}) }}>
      <div styleName="info-left" style={isEditing ? { width: '100%', marginRight: 0 } : {}}>
        <span styleName="info-left-top" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {icon}
          {label}
        </span>
        {!isEditing ? (
          <span styleName="info-left-bottom" onClick={handleEditClick} style={{ cursor: isRestricted ? 'default' : 'pointer', fontFamily: '"Roboto Slab"', textTransform: type === 'email' ? 'none' : 'capitalize' }}>{value || '\u00A0'}</span>
        ) : (
          <div styleName="inputGroup" style={{ position: 'relative' }}>
            {prefixBlock ? (
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', width: '100%', paddingBottom: '4px' }}>
                  {prefixBlock}
                  <input
                    ref={inputRef}
                    type={type}
                    styleName={error ? "edit-input errorText" : "edit-input"}
                    style={{ flex: 1, fontFamily: '"Roboto Slab"', textTransform: type === 'email' ? 'none' : 'capitalize' }}
                    value={editValue}
                    onChange={(e) => {
                      setEditValue(e.target.value);
                      if (onValidate) onValidate(e.target.value, true);
                    }}
                  />
                </div>
                <div styleName={error ? "editInputUnderlineError" : "editInputUnderline"}></div>
              </div>
            ) : (
              <>
                <input
                  ref={inputRef}
                  type={type}
                  styleName={error ? "edit-input errorText" : "edit-input"}
                  style={{ fontFamily: '"Roboto Slab"', textTransform: type === 'email' ? 'none' : 'capitalize' }}
                  value={editValue}
                  onChange={(e) => {
                    setEditValue(e.target.value);
                    if (onValidate) onValidate(e.target.value, true);
                  }}
                />
                <div styleName={error ? "editInputUnderlineError" : "editInputUnderline"}></div>
              </>
            )}
            {error && (
              <div styleName="errorMessage" style={{ marginTop: "5px" }}>
                {errorMessage}
              </div>
            )}

            {showOtp && (
              <div style={{ marginTop: '15px', marginBottom: '15px' }}>
                <input
                  type="text"
                  placeholder="Enter OTP"
                  styleName="edit-input otpInput"
                  value={otp}
                  maxLength={6}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                />
                <div styleName="editInputUnderline"></div>
                {otpError && (
                  <div styleName="errorMessage" style={{ marginTop: "5px" }}>
                    {otpErrorMessage}
                  </div>
                )}
              </div>
            )}

            <div styleName="otpButtonContainer">
              {showOtp ? (
                <>
                  <button
                    type="button"
                    styleName={isUpdating ? "nameDoneBtnLoading" : (!otp ? "nameDoneBtnDisabled" : "nameDoneBtn")}
                    onClick={handleVerifyOtp}
                    disabled={isUpdating || !otp}
                  >
                    {isUpdating ? (
                      <>
                        <SpinnerIcon styleName="spinner" color="#FFF" />
                        Verifying
                      </>
                    ) : (
                      "Verify OTP"
                    )}
                  </button>
                  <button type="button" styleName="nameCancelBtn" onClick={handleCancel}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    styleName={isUpdating ? "nameDoneBtnLoading" : (((typeof editValue === 'string' ? editValue.trim() : editValue) === (typeof value === 'string' ? value.trim() : value)) || (type === 'tel' && (typeof editValue === 'string' ? editValue.trim() : editValue).length !== 10) ? "nameDoneBtnDisabled" : "nameDoneBtn")}
                    onClick={handleSaveClick}
                    disabled={isUpdating || (typeof editValue === 'string' ? editValue.trim() : editValue) === (typeof value === 'string' ? value.trim() : value) || (type === 'tel' && (typeof editValue === 'string' ? editValue.trim() : editValue).length !== 10)}
                  >
                    {isUpdating ? (
                      <>
                        <SpinnerIcon styleName="spinner" color="#FFF" />
                        Verifying
                      </>
                    ) : requiresOtp ? (
                      "Send OTP"
                    ) : (
                      "Update"
                    )}
                  </button>
                  <button type="button" styleName="nameCancelBtn" onClick={handleCancel}>
                    Cancel
                  </button>
                </>
              )}
            </div>

            {showOtp && (
              <div style={{ marginTop: '15px', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                {timer > 0 ? (
                  <>
                    <span styleName="resendOtpTextDisabled">Resend OTP</span>
                    <TimerIcon seconds={timer} />
                  </>
                ) : (
                  <span
                    styleName="resendOtpText"
                    onClick={() => {
                      if (onSendOtp) {
                        onSendOtp(editValue);
                        setTimer(30);
                      }
                    }}
                  >
                    Resend OTP
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <button styleName="editIconButton" style={{ cursor: isRestricted ? 'default' : 'pointer' }} onClick={handleEditClick}>
        <div style={{ pointerEvents: 'none', display: 'flex' }}><EditIcon width="18" height="18" color="#0091AC" /></div>
      </button>
    </div>
  );
};


const MyProfile = () => {
  const [activeTab, setActiveTab] = useState('personal');
  const dispatch = useDispatch();
  const userdetails = useSelector((state) => get(state, ["user", "user"], {}));
  const isUserLogin = useSelector((state) => get(state, ["isLogin"], false));
  const ssoEndpoint = useSelector((state) => get(state, ["qt", "config", "publisher-attributes", "sso_endpoint"], null));

  const [isLoadingAuth, setIsLoadingAuth] = useState(true);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const hasSession = !!getAccessToken() || !!window.localStorage.getItem("user");
      if (isUserLogin || hasSession) {
        setIsLoadingAuth(false);
      } else {
        window.location.href = "/";
      }
    }
  }, [isUserLogin]);

  const extractActualUser = (u) => {
    if (!u) return {};
    if (u.metadata || u.userData) return u;
    if (u.user && (u.user.metadata || u.user.userData)) return u.user;
    if (u.user?.user && (u.user.user.metadata || u.user.user.userData)) return u.user.user;
    return u;
  };

  const actualUser = extractActualUser(userdetails);

  // Initial Data Sync: Check localStorage instantly, then fetch fresh data from API
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const lsUser = JSON.parse(window.localStorage.getItem("user") || "{}");
        if (lsUser?.metadata || lsUser?.userData) {
          dispatch(setUserDetails(lsUser));
        }
      } catch (e) {}

      // Fetch fresh data in the background
      getUserDetails()
        .then((data) => {
          if (data && (data.metadata || data.userData)) {
            dispatch(setUserDetails(data));
            window.localStorage.setItem("user", JSON.stringify(data));
          }
        })
        .catch((err) => console.error("Error fetching fresh user details", err));
    }
  }, [dispatch]);

  const isGoogleUser =
    actualUser?.metadata?.login_method === "google_login" ||
    actualUser?.metadata?.login_method === "google";

  const PAYMENT_PROCESSING_STATE_KEY = "subscribe_payment_processing_state";
  const SHOW_SUPPORT_STRIP_ON_REDIRECT_KEY = "show_support_strip_on_redirect";

  // Extract initial values from actualUser
  const initialFirstName = actualUser?.metadata?.first_name || '';
  const initialDisplayName = actualUser?.metadata?.displayName || '';
  const initialMobile = actualUser?.userData?.phoneNumbers?.[0] || '';
  const initialEmail = actualUser?.userData?.emails?.[0] || '';
  const initialGender = actualUser?.metadata?.gender || 'Prefer not to say';
  let initialDob = null;
  const dateObj = actualUser?.metadata?.dob;
  if (dateObj?.year) {
    initialDob = new Date(dateObj.year, dateObj.month - 1, dateObj.day);
  }

  // Name States
  const [firstName, setFirstName] = useState(initialFirstName);
  const [nameError, setNameError] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState("");
  const [isUpdatingName, setIsUpdatingName] = useState(false);

  // Display Name States
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [displayNameError, setDisplayNameError] = useState(false);
  const [displayNameErrorMessage, setDisplayNameErrorMessage] = useState("");
  const [isUpdatingDisplayName, setIsUpdatingDisplayName] = useState(false);

  // Mobile States
  const [mobileNumber, setMobileNumber] = useState(initialMobile);
  const [mobileError, setMobileError] = useState(false);
  const [mobileErrorMessage, setMobileErrorMessage] = useState("");
  const [authSessionInfo, setAuthSessionInfo] = useState({ preAuthSessionId: null, deviceId: null });
  const [mobileOtpError, setMobileOtpError] = useState(false);
  const [mobileOtpErrorMessage, setMobileOtpErrorMessage] = useState("");
  const [isUpdatingMobile, setIsUpdatingMobile] = useState(false);

  // Email States
  const [email, setEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [emailOtpError, setEmailOtpError] = useState(false);
  const [emailOtpErrorMessage, setEmailOtpErrorMessage] = useState("");
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);

  // Gender State
  const [gender, setGender] = useState(initialGender);

  // Date of Birth States
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [birthDate, setBirthDate] = useState(initialDob);
  const [savedDob, setSavedDob] = useState(initialDob);
  const [dobDone, setDobDone] = useState(true);
  const [isUpdatingDob, setIsUpdatingDob] = useState(false);
  const [dobError, setDobError] = useState(false);
  const [dobErrorMessage, setDobErrorMessage] = useState("");

  useEffect(() => {
    if (actualUser?.metadata || actualUser?.userData) {
      setFirstName(actualUser?.metadata?.first_name || '');
      setDisplayName(actualUser?.metadata?.displayName || '');
      setMobileNumber(actualUser?.userData?.phoneNumbers?.[0] || '');
      setEmail(actualUser?.userData?.emails?.[0] || '');
      setGender(actualUser?.metadata?.gender || 'Prefer not to say');

      const dateObj = actualUser?.metadata?.dob;
      if (dateObj?.year) {
        const d = new Date(dateObj.year, dateObj.month - 1, dateObj.day);
        setBirthDate(d);
        setSavedDob(d);
      }
    }
  }, [actualUser]);

  const updateReduxAndLocal = (keyToUpdate, newValue) => {
    let updatedUser;

    // Check if the key being updated is part of userData or metadata
    if (keyToUpdate === 'email') {
      updatedUser = {
        ...actualUser,
        userData: { ...actualUser?.userData, emails: [newValue] }
      };
    } else if (keyToUpdate === 'mobile') {
      updatedUser = {
        ...actualUser,
        userData: { ...actualUser?.userData, phoneNumbers: [newValue] }
      };
    } else {
      updatedUser = {
        ...actualUser,
        metadata: { ...actualUser?.metadata, [keyToUpdate]: newValue }
      };
    }

    dispatch(setUserDetails(updatedUser));
    if (typeof window !== "undefined") {
      window.localStorage.setItem("user", JSON.stringify(updatedUser));
    }
  };

  const dispatchSnackbar = (type, message) => {
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("show-snackbar", { detail: { type, message } }));
    }, 100);
  };

  // Validations
  const validateName = (val, reset = false) => {
    if (reset) {
      setNameError(false);
      setNameErrorMessage("");
      return true;
    }
    if (!val || val.length < 2) {
      setNameError(true);
      setNameErrorMessage("Please enter at least 2 characters.");
      return false;
    }
    if (/\d/.test(val)) {
      setNameError(true);
      setNameErrorMessage("Please enter a valid name");
      return false;
    }
    setNameError(false);
    return true;
  };

  const validateDisplayName = (val, reset = false) => {
    if (reset) {
      setDisplayNameError(false);
      setDisplayNameErrorMessage("");
      return true;
    }
    if (!val || val.length < 2) {
      setDisplayNameError(true);
      setDisplayNameErrorMessage("Please enter at least 2 characters.");
      return false;
    }
    setDisplayNameError(false);
    return true;
  };

  const validateMobile = (val, reset = false) => {
    if (reset) {
      setMobileError(false);
      setMobileErrorMessage("");
      return true;
    }
    if (!/^\d{10}$/.test(val)) {
      setMobileError(true);
      setMobileErrorMessage("Enter a valid 10-digit indian mobile number");
      return false;
    }
    setMobileError(false);
    return true;
  };

  const validateEmail = (val, reset = false) => {
    if (reset) {
      setEmailError(false);
      setEmailErrorMessage("");
      return true;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      setEmailError(true);
      setEmailErrorMessage("Invalid email address.");
      return false;
    }
    setEmailError(false);
    return true;
  };

  // Updaters
  const handleUpdateName = async (val) => {
    setIsUpdatingName(true);
    try {
      const res = await putUserDetails({ first_name: val });
      if (res && res.errorMessage) {
        setNameError(true);
        setNameErrorMessage(res.errorMessage);
      } else {
        setFirstName(val);
        updateReduxAndLocal('first_name', val);
        dispatchSnackbar('success', 'Name updated successfully!');
      }
    } catch (e) {
      console.error("Failed to update Name:", e);
      dispatchSnackbar('failure', e.message || 'Failed to update Name. Please try again.');
    } finally {
      setIsUpdatingName(false);
    }
  };

  const handleUpdateDisplayName = async (val) => {
    setIsUpdatingDisplayName(true);
    try {
      const res = await putUserDetails({ displayName: val });
      if (res && res.errorMessage) {
        setDisplayNameError(true);
        setDisplayNameErrorMessage(res.errorMessage);
      } else {
        setDisplayName(val);
        updateReduxAndLocal('displayName', val);
        dispatchSnackbar('success', 'Display Name updated successfully!');
      }
    } catch (e) {
      console.error("Failed to update Display Name:", e);
      dispatchSnackbar('failure', e.message || 'Failed to update Display Name. Please try again.');
    } finally {
      setIsUpdatingDisplayName(false);
    }
  };

  const handleUpdateGender = async (val) => {
    setGender(val);
    try {
      const res = await putUserDetails({ gender: val });
      if (!res?.errorMessage) {
        updateReduxAndLocal('gender', val);
        dispatchSnackbar('success', 'Gender updated successfully!');
      }
    } catch (e) {
      console.error("Failed to update Gender:", e);
      dispatchSnackbar('failure', e.message || 'Failed to update Gender. Please try again.');
    }
  };

  const handleUpdateDob = async () => {
    if (birthDate && birthDate > new Date()) {
      setDobError(true);
      setDobErrorMessage("Invalid Date, Please select a Valid Date");
      return;
    }
    setDobError(false);
    setIsUpdatingDob(true);

    try {
      const dateValue = { year: birthDate.getFullYear(), month: birthDate.getMonth() + 1, day: birthDate.getDate() };
      const res = await putUserDetails({ dob: dateValue });
      if (res && res.errorMessage) {
        setDobError(true);
        setDobErrorMessage(res.errorMessage);
      } else {
        setSavedDob(birthDate);
        setDobDone(true);
        updateReduxAndLocal('dob', dateValue);
        dispatchSnackbar('success', 'Date of Birth updated successfully!');
      }
    } catch (e) {
      console.error("Failed to update Date of Birth:", e);
      dispatchSnackbar('failure', e.message || 'Failed to update Date of Birth. Please try again.');
    } finally {
      setIsUpdatingDob(false);
    }
  };
  // OTP Handlers for Email/Mobile
  const handleSendMobileOtp = async (val) => {
    setIsUpdatingMobile(true);
    setMobileOtpError(false);
    setMobileOtpErrorMessage("");
    try {
      const res = await changePhoneOtp(val);
      setAuthSessionInfo({ preAuthSessionId: res?.preAuthSessionId, deviceId: res?.deviceId });
      dispatchSnackbar('info', 'OTP has been sent to your Phone Number');
      return true;
    } catch (e) {
      console.error("Failed to send Mobile OTP:", e);
      if (e.status === 409) {
        setMobileError(true);
        setMobileErrorMessage("User already exist with given Phone Number");
        throw e;
      }
      const errorMsg = e.message || 'Failed to send OTP. Please try again.';
      setMobileOtpError(true);
      setMobileOtpErrorMessage(errorMsg);
      dispatchSnackbar('failure', errorMsg);
      throw e;
    } finally {
      setIsUpdatingMobile(false);
    }
  };

  const handleVerifyMobileOtp = async (val, otpVal) => {
    setIsUpdatingMobile(true);
    setMobileOtpError(false);
    setMobileOtpErrorMessage("");
    try {
      await verifySsoCode({ preAuthSessionId: authSessionInfo.preAuthSessionId, deviceId: authSessionInfo.deviceId, userInputCode: otpVal });
      setMobileNumber(val);
      updateReduxAndLocal('mobile', val);
      dispatchSnackbar('success', 'Mobile Number updated successfully!');
    } catch (e) {
      console.error("Failed to verify Mobile OTP:", e);
      if (e.status === 409) {
        setMobileError(true);
        setMobileErrorMessage("User already exist with given Phone Number");
        return;
      }
      const errorMsg = e.message || 'Failed to verify OTP. Please try again.';
      setMobileOtpError(true);
      setMobileOtpErrorMessage(errorMsg);
      throw e;
    } finally {
      setIsUpdatingMobile(false);
    }
  };

  const handleSendEmailOtp = async (val) => {
    setIsUpdatingEmail(true);
    setEmailOtpError(false);
    setEmailOtpErrorMessage("");
    try {
      const res = await changeEmailOtp(val);
      setAuthSessionInfo({ preAuthSessionId: res?.preAuthSessionId, deviceId: res?.deviceId });
      dispatchSnackbar('info', 'OTP has been sent to your Email ID');
      return true;
    } catch (e) {
      console.error("Failed to send Email OTP:", e);
      if (e.status === 409) {
        setEmailError(true);
        setEmailErrorMessage("User already exist with given Email ID.");
        throw e;
      }
      const errorMsg = e.message || 'Failed to send OTP. Please try again.';
      setEmailOtpError(true);
      setEmailOtpErrorMessage(errorMsg);
      dispatchSnackbar('failure', errorMsg);
      throw e;
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleVerifyEmailOtp = async (val, otpVal) => {
    setIsUpdatingEmail(true);
    setEmailOtpError(false);
    setEmailOtpErrorMessage("");
    try {
      await verifySsoCode({ preAuthSessionId: authSessionInfo.preAuthSessionId, deviceId: authSessionInfo.deviceId, userInputCode: otpVal });
      setEmail(val);
      updateReduxAndLocal('email', val);
      dispatchSnackbar('success', 'Email updated successfully!');
    } catch (e) {
      console.error("Failed to verify Email OTP:", e);
      if (e.status === 409) {
        setEmailError(true);
        setEmailErrorMessage("User already exist with given Email ID.");
        return;
      }
      const errorMsg = e.message || 'Failed to verify OTP. Please try again.';
      setEmailOtpError(true);
      setEmailOtpErrorMessage(errorMsg);
      throw e;
    } finally {
      setIsUpdatingEmail(false);
    }
  };
  // E-Paper state
  const [isEpaperOpen, setIsEpaperOpen] = useState(false);
  const [selectedEpaper, setSelectedEpaper] = useState("");
  const [selectedEpaperId, setSelectedEpaperId] = useState("");
  const [editionList, setEditionList] = useState([]);
  const [isEpaperLoading, setIsEpaperLoading] = useState(true);

  useEffect(() => {
    setIsEpaperLoading(true);
    Promise.all([getEpaperEditions(), getEpaperPreferences()])
      .then(([editionsRes, prefsRes]) => {
        // Flatten the nested editions array
        const list = [];
        const rawData = editionsRes?.data || editionsRes || [];
        if (Array.isArray(rawData)) {
          rawData.forEach((parent) => {
            if (parent.editions && Array.isArray(parent.editions)) {
              parent.editions.forEach((ed) => {
                list.push({ id: ed.edition_number, name: ed.edition_name });
              });
            }
          });
        }
        setEditionList(list);

        // Set the user's current preference if they have one
        const prefId = prefsRes?.data?.dh_preferred_edition_id;
        if (prefId) {
          setSelectedEpaperId(prefId);
          const match = list.find((e) => String(e.id) === String(prefId));
          if (match) setSelectedEpaper(match.name);
        } else if (list.length > 0) {
          // Fallback default to edition 2 (Bengaluru City)
          const defaultEdition = list.find((e) => String(e.id) === "2");
          if (defaultEdition) {
            setSelectedEpaper(defaultEdition.name);
            setSelectedEpaperId(defaultEdition.id);
          } else {
            setSelectedEpaper(list[0].name);
            setSelectedEpaperId(list[0].id);
          }
        }
      })
      .catch((err) => console.error("Error loading epaper data", err))
      .finally(() => setIsEpaperLoading(false));
  }, []);

  const handleLogout = () => {
    addLogoutToDataLayer(window.location.href, userdetails, "profile");
    performClientLogout(dispatch);

    let redirectUrl = window.location.origin + "/";
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get("returnUrl");

    if (returnUrl) {
      redirectUrl = returnUrl;
    } else if (document.referrer && document.referrer.includes(window.location.origin)) {
      redirectUrl = document.referrer;
    }

    setTimeout(() => {
      window.location.search = "";
      if (ssoEndpoint) {
        window.location.href = `${ssoEndpoint}/auth/logout?redirectUrl=${redirectUrl}`;
      } else {
        window.location.href = redirectUrl;
      }
    }, 300);
  };

  const handleEpaperChange = (edition) => {
    // Optimistically update UI
    setSelectedEpaper(edition.name);
    setSelectedEpaperId(edition.id);
    setIsEpaperOpen(false);
    // Fire API
    updateEpaperPreferences(edition.id)
      .then(() => {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("show-snackbar", {
            detail: { type: "success", message: "Edition updated successfully!" }
          }));
        }, 100);
      })
      .catch(() => {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("show-snackbar", {
            detail: { type: "failure", message: "Failed to update edition." }
          }));
        }, 100);
      });
  };

  if (isLoadingAuth) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <SpinnerIcon color="#0091ac" />
      </div>
    );
  }

  return (
    <div>
      <div styleName="profileHeader">
        <a href="#" onClick={(e) => { e.preventDefault(); window.history.back(); }} styleName="backButton">
          <Arrow width="18px" height="18px" color="#000" />
        </a>
        <h1 styleName="profileTitle">My Profile</h1>
      </div>

      <div styleName="tabContainer">
        <button styleName={activeTab === 'personal' ? 'tabButton active' : 'tabButton'} onClick={() => setActiveTab('personal')}>
          <span styleName="desktopTabText">Personal Information</span>
          <span styleName="mobileTabText">Personal Info</span>
        </button>
        <button
          styleName={activeTab === 'subscriptions' ? 'tabButton active' : 'tabButton'}
          onClick={() => setActiveTab('subscriptions')}
        >
          Subscriptions
        </button>
      </div>

      {activeTab === 'personal' && (
        <div styleName="formContainer">
      <ProfileField
        label="Name"
        value={firstName}
        type="text"
        icon={<User />}
        error={nameError}
        errorMessage={nameErrorMessage}
        isUpdating={isUpdatingName}
        onValidate={validateName}
        onSave={handleUpdateName}
        isRestricted={isGoogleUser}
        restrictedMessage="Name is not editable when logged in via Google."
      />
      <ProfileField
        label="Mobile Number"
        value={mobileNumber}
        type="tel"
        prefixBlock={
          <div style={{ display: 'flex', width: '32px', height: '26px', padding: '1px 2px', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '10px', borderRadius: '3px', background: '#D9D9D9', color: '#666', fontFamily: 'Arial', fontSize: '14px', fontStyle: 'normal', fontWeight: '400', lineHeight: '22px', letterSpacing: '-0.42px', textTransform: 'capitalize', marginRight: '10px' }}>
            +91
          </div>
        }
        requiresOtp={true}
        icon={<PhoneIcon />}
        error={mobileError}
        errorMessage={mobileErrorMessage}
        otpError={mobileOtpError}
        otpErrorMessage={mobileOtpErrorMessage}
        isUpdating={isUpdatingMobile}
        onValidate={validateMobile}
        onSendOtp={handleSendMobileOtp}
        onVerifyOtp={handleVerifyMobileOtp}
      />
      <ProfileField
        label="Email ID"
        value={email}
        type="email"
        requiresOtp={true}
        icon={<MailIcon />}
        error={emailError}
        errorMessage={emailErrorMessage}
        otpError={emailOtpError}
        otpErrorMessage={emailOtpErrorMessage}
        isUpdating={isUpdatingEmail}
        onValidate={validateEmail}
        onSendOtp={handleSendEmailOtp}
        onVerifyOtp={handleVerifyEmailOtp}
        isRestricted={isGoogleUser}
        restrictedMessage="Cannot update email as you are logged in via Google."
      />
      <ProfileField
        label="Display Name"
        value={displayName}
        type="text"
        icon={<NewUserIcon />}
        error={displayNameError}
        errorMessage={displayNameErrorMessage}
        isUpdating={isUpdatingDisplayName}
        onValidate={validateDisplayName}
        onSave={handleUpdateDisplayName}
      />

      {/* DOB DatePicker */}
      <div styleName="info-container" style={!dobDone ? { borderBottom: "none", cursor: "pointer", display: "block", paddingBottom: "15px" } : { borderBottom: "1px solid #E0EEFB", cursor: "pointer", display: "block" }} onClick={() => setIsCalendarOpen(true)}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span styleName="info-left-top" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="hide-mobile" styleName="desktop-svg">
              <EmptyCalenderIcon />
            </span>
            Date of Birth
          </span>
          <span style={{ display: "flex", alignItems: "center" }}>
            <Calender width="18px" height="18px" color="#0091AC" />
          </span>
        </div>

        <span styleName="info-left-bottom" style={{ display: "block", minHeight: "22px" }}>
          {birthDate ? birthDate.toLocaleDateString("en-IN") : " "}
        </span>

        {dobError && (
          <div styleName="errorMessage" style={{ marginTop: "5px" }}>
            {dobErrorMessage}
          </div>
        )}

        {!dobDone && (
          <div styleName="otpButtonContainer">
            <button
              type="button"
              styleName={isUpdatingDob ? "nameDoneBtnLoading" : "nameDoneBtn"}
              onClick={(e) => {
                e.stopPropagation();
                handleUpdateDob();
              }}
              disabled={isUpdatingDob}
            >
              {isUpdatingDob ? (
                <>
                  <SpinnerIcon styleName="spinner" color="#FFF" />
                  Updating
                </>
              ) : (
                "Update"
              )}
            </button>
            <button
              type="button"
              styleName="nameCancelBtn"
              onClick={(e) => {
                e.stopPropagation();
                setBirthDate(savedDob);
                setDobDone(true);
                setDobError(false);
              }}
            >
              Cancel
            </button>
          </div>
        )}
        {/* Calendar Popup Component */}
        <BirthDatePicker
          isOpen={isCalendarOpen}
          onClose={() => setIsCalendarOpen(false)}
          birthDate={birthDate}
          setBirthDate={(date) => {
            setBirthDate(date);
            setDobError(false);
            setDobDone(false); // Reveals the save buttons below the date
          }}
        />
      </div>

      {/* Gender Radio Buttons */}
      <div styleName="info-container" style={{ borderBottom: '1px solid #E0EEFB', paddingBottom: '5px' }}>
        <div styleName="info-left">
          <span styleName="info-left-top" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <GenderIcon /> Gender
          </span>
          <div styleName="radio-group">
            {['Male', 'Female', 'Prefer not to say'].map(opt => (
              <label key={opt} styleName="radio-item">
                <input
                  type="radio"
                  name="gender"
                  value={opt}
                  checked={gender?.toLowerCase() === opt.toLowerCase()}
                  onChange={(e) => handleUpdateGender(e.target.value)}
                  styleName="radio-input"
                />
                <div styleName="radio-circle"></div>
                {opt}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* E-Paper Edition Preference */}
      <div styleName="info-container" style={{ borderBottom: isEpaperOpen ? "none" : "" }}>
        <div styleName="info-left" style={{ width: "100%", marginRight: 0 }}>
          <span styleName="info-left-top" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: "10px" }}>
            <span className="hide-mobile" styleName="desktop-svg">
              <LocationPinIcon height="20" width="16" />
            </span>
            Epaper Preferred Edition
          </span>

          <div styleName={`info-left-bottom ${isEpaperOpen ? "spcl-border" : ""}`}>
            <div styleName="custom-dropdown">

              {/* Trigger Button */}
              <div styleName="dropdown-header" onClick={() => setIsEpaperOpen(true)}>
                <span>{isEpaperLoading ? "Loading..." : selectedEpaper}</span>
                {!isEpaperOpen ? <FillDownArrow height="13px" width="13px" color="#0091AC" /> : <CityIcon color="#999999" />}
              </div>

              {/* Dropdown Modal */}
              {isEpaperOpen && (
                <div styleName="edition-overlay" onClick={() => setIsEpaperOpen(false)}>
                  <div styleName="edition-modal" onClick={(e) => e.stopPropagation()}>

                    <div styleName="edition-header">
                      <span>Select Edition</span>
                      <button styleName="close-btn" onClick={() => setIsEpaperOpen(false)}>
                        <Close height="10px" width="10px" color="black" />
                      </button>
                    </div>

                    <div styleName="edition-divider" />
                    <div styleName="edition-state">Karnataka</div>

                    <div styleName="edition-list">
                      {isEpaperLoading ? (
                        <div styleName="edition-item">Loading editions...</div>
                      ) : (
                        editionList.map((edition) => (
                          <div
                            key={edition.id}
                            styleName={`edition-item ${String(selectedEpaperId) === String(edition.id) ? "selected" : ""}`}
                            onClick={() => {
                              handleEpaperChange(edition);
                              setIsEpaperOpen(false);
                            }}
                          >
                            <div styleName="edition-left">
                              <LocationPinIcon />
                              <span>{edition.name}</span>
                            </div>
                            {String(selectedEpaperId) === String(edition.id) && (
                              <span styleName="checkmark">
                                <TickIcon />
                              </span>
                            )}
                          </div>
                        ))
                      )}
                    </div>

                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>


      </div>
      )}

      {activeTab === 'subscriptions' && (
        <MySubscription />
      )}

      <div styleName="logoutWrapper" style={{ marginTop: '20px' }}>
        <button styleName="logout-button" onClick={handleLogout}>Logout</button>
      </div>
      <Snackbar />
    </div>
  );
};

export default MyProfile;
